import { useCallback, useState } from 'react';
import { message } from 'antd';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import type { TaskStore } from '../../stores/kanban/TaskStore';

export type QuickAdd = {
  /** Status whose inline "add task" form is open, or null. */
  openInStatus: string | null;
  open: (statusId: string) => void;
  cancel: () => void;
  submit: (
    statusId: string,
    title: string,
    description: string,
    dueDate?: string | null,
    assigneeId?: number | null,
  ) => Promise<void>;
};

/** The inline "add task" box at the foot of a column. */
export function useQuickAdd(
  store: TaskStore,
  projectId: string | undefined,
  currentUserId: string | undefined,
): QuickAdd {
  const { t } = useTranslation();
  const [openInStatus, setOpenInStatus] = useState<string | null>(null);

  const submit = useCallback(
    async (
      statusId: string,
      title: string,
      description: string,
      dueDate?: string | null,
      assigneeId?: number | null,
    ): Promise<void> => {
      const projectIdNumber = Number(projectId);
      if (!projectId || Number.isNaN(projectIdNumber)) {
        message.error('Invalid project ID');
        return;
      }
      try {
        await store.createTask({
          title,
          // Already HTML — `InlineTaskForm` converts the plain-text box before it gets here.
          description: description || '',
          projectId: projectIdNumber,
          statusId: Number(statusId),
          priority: 1,
          // The server stamps the author from the caller's token (`CreateKanbanTaskDto` carries no
          // author at all), so the public board sends 0 instead of the old hard-coded user 5.
          createdByUserId: currentUserId ? Number(currentUserId) : 0,
          startDate: dayjs().format('YYYY-MM-DD HH:mm'),
          dueDate: dueDate ? dayjs(dueDate).format('YYYY-MM-DD HH:mm') : undefined,
          assigneeId: assigneeId ? Number(assigneeId) : undefined,
        });
        message.success(t('TaskCreated'));
        setOpenInStatus(null);
      } catch (error) {
        console.error(error);
        message.error(t('FailedToCreateTask'));
      }
    },
    [store, projectId, currentUserId, t],
  );

  return {
    openInStatus,
    open: useCallback((statusId: string) => setOpenInStatus(statusId), []),
    cancel: useCallback(() => setOpenInStatus(null), []),
    submit,
  };
}
